import type { Props as ModalProps } from '#brands/components/vf/modal/Modal.vue'
import type { Props as PanelProps } from '#brands/components/vf/panel/Panel.vue'
import type { Dialogs } from '#build/dialogs'
import type { DialogBaseOptions } from '#types/config/components/dialog'
import { VfModal, VfPanel } from '#components'
import { createDialog as _createDialog } from '#core/utils/createDialog'

interface DialogOptions {
  classContent?: DialogBaseOptions['classContent']
  depth?: DialogBaseOptions['depth']
  position?: DialogBaseOptions['position']
  size?: DialogBaseOptions['size']
  teleport?: MaybeRefOrGetter<boolean>
  singleton?: boolean
  type?: DialogBaseOptions['type']
  variant?: DialogBaseOptions['variant']
}

/**
 * Creates a dialog component based on the provided options.
 * @param name The name of the dialog.
 * @param {DialogOptions} options Options for configuring the dialog.
 * @returns A dialog component.
 * @category Utils
 */
export function createDialog<Name extends keyof Dialogs, WrapperProps>(
  name: Name,
  options?: DialogOptions
) {
  return _createDialog<Name, WrapperProps, Dialogs>(name, {
    transition: {
      name: 'nested',
      appear: true,
      duration: 250
    },
    ...options,
    wrapper: () => {
      const { $viewport } = useNuxtApp()
      const { dialogOptions: defaultOptions } = useAppConfig().components.dialog.default
      const wrapperValue = toValue(options?.type) ?? defaultOptions.type

      const _wrapper = isObject(wrapperValue)
        ? getValueForBreakpoint($viewport.breakpoint, wrapperValue)
        : wrapperValue

      return _wrapper
        ? {
            modal: VfModal,
            panel: VfPanel
          }[_wrapper]
        : null
    }
  })
}

/**
 * Creates a modal component based on the provided options.
 * @param name The name of the modal.
 * @param {Omit<DialogOptions, 'type'>} options Options for configuring the dialog.
 * @returns A modal component.
 */
export function createModal<Name extends keyof Dialogs>(
  name: Name,
  options?: Omit<DialogOptions, 'type'>
) {
  return createDialog<Name, ModalProps>(name, { ...options, type: 'modal' })
}

/**
 * Creates a panel component based on the provided options.
 * @param name The name of the panel.
 * @param {Omit<DialogOptions, 'type'>} options Options for configuring the dialog.
 * @returns A panel component.
 */
export function createPanel<Name extends keyof Dialogs>(
  name: Name,
  options?: Omit<DialogOptions, 'type'>
) {
  return createDialog<Name, PanelProps>(name, { ...options, type: 'panel' })
}
